import type { Tone } from "shared/tones";
import type { ConductorRole } from "../api/MeetingsApi";

/** meeting_date arrives as "YYYY-MM-DD"; parsing it with `new Date()` treats it
 * as UTC midnight and shifts the displayed day in negative-offset timezones, so
 * build a local date from the parts instead. */
export function formatMeetingDate(value: string | null | undefined, withYear = true): string {
  if (!value) return "—";
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return value;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: withYear ? "numeric" : undefined,
    month: "short",
    day: "numeric",
  });
}

export const CONDUCTOR_TONE: Record<ConductorRole, Tone> = {
  CEO: "brand",
  HR: "info",
  "Team Lead": "teal",
  "Technical Manager": "magenta",
  Other: "neutral",
};

export function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("");
}

export const OVERDUE_DAYS = 90;

function localIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** True when the employee has never had a meeting, or the last one was more than OVERDUE_DAYS days ago. */
export function hasNoRecentMeeting(lastMeetingDate: string | null | undefined, now: Date = new Date()): boolean {
  if (!lastMeetingDate) return true;
  const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate() - OVERDUE_DAYS);
  return lastMeetingDate.slice(0, 10) < localIso(cutoff);
}

export type OverdueTier = "never" | "stale";

/** "never": no meeting on record; "stale": the last one is older than OVERDUE_DAYS; null: up to date. */
export function overdueTier(lastMeetingDate: string | null | undefined, now: Date = new Date()): OverdueTier | null {
  if (!lastMeetingDate) return "never";
  return hasNoRecentMeeting(lastMeetingDate, now) ? "stale" : null;
}

/** Whole days from a "YYYY-MM-DD" date to today (local); 0 for today, never negative. */
export function daysSince(value: string, now: Date = new Date()): number {
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  const then = Date.UTC(y, m - 1, d);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.round((today - then) / 86400000));
}

export function formatDaysAgo(value: string | null | undefined, now: Date = new Date()): string {
  if (!value) return "";
  const n = daysSince(value, now);
  return n === 0 ? "today" : n === 1 ? "1 day ago" : `${n} days ago`;
}

/** Employees who need a meeting first (never met, then oldest last meeting), the rest by name. */
export function sortByNeed<T extends { name: string; last_meeting_date: string | null }>(employees: T[]): T[] {
  return [...employees].sort((a, b) => {
    const ka = a.last_meeting_date ? a.last_meeting_date.slice(0, 10) : "";
    const kb = b.last_meeting_date ? b.last_meeting_date.slice(0, 10) : "";
    const ta = overdueTier(a.last_meeting_date), tb = overdueTier(b.last_meeting_date);
    if (!!ta !== !!tb) return ta ? -1 : 1;
    if (ta && tb && ka !== kb) return ka < kb ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}
